import { times, map, filter, flatMap, uniq, isEmpty, get, compact, head } from 'lodash';
import { INDICATION_TYPES } from 'taboola-ultimate-ui';
import { addIndication } from 'modules/taboola-common-frontend-modules/Indications';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import getChangesObject from 'modules/taboola-common-frontend-modules/utils/objectDiff';
import { bulkOperationsApi } from 'services/api';
import { errorMessagesUtils } from 'services/utils';
import {
    duplicateCampaignsGroupError,
    duplicateCampaignsGroupSuccess,
} from '../../campaigns-group/actions/duplicateCampaignsGroup';
import { editedCampaignsGroupSelector } from '../../campaigns-group/selectors';

const generateErrorIndication = (errorObject = {}) => ({
    message: errorMessagesUtils.getErrorMessage(errorObject),
    type: INDICATION_TYPES.ERROR,
    highlight: (
        <FormattedMessage
            id="campaigns.group.duplicate.error"
            defaultMessage="Something went wrong trying to duplicate the campaign group."
        />
    ),
});

const generateFailedCampaignWarningIndication = failedCampaignIds => ({
    message: (
        <FormattedMessage
            id="campaigns.group.duplicate.failed.campaigns.warning"
            defaultMessage="Failed campaigns: {failedCampaignIds}."
            values={{ failedCampaignIds }}
        />
    ),
    type: INDICATION_TYPES.WARNING,
    highlight: (
        <FormattedMessage
            id="campaigns.group.duplicate.failed.campaigns.warning.highlight"
            defaultMessage="Campaign group was duplicated but some campaigns failed."
        />
    ),
});

const successIndication = {
    message: (
        <FormattedMessage
            id="campaigns.group.duplicate.success"
            defaultMessage="Campaign group was successfully duplicated."
        />
    ),
    type: INDICATION_TYPES.SUCCESS,
    highlight: <FormattedMessage id="success.highlight" defaultMessage="Success!" />,
};

export const duplicateCampaignGroup = (numericAccountId, accountName, campaignsGroup) => async (dispatch, getState) => {
    const campaignsGroupBeingEdited = editedCampaignsGroupSelector(getState());
    const campaignsGroupChanges = getChangesObject(campaignsGroup, campaignsGroupBeingEdited);
    const { duplicateCampaignsGroups } = bulkOperationsApi;

    const campaignGroupsWithDuplicationSettings = transformCampaignGroupsWithDuplicationSettings(
        campaignsGroup,
        campaignsGroupChanges,
        numericAccountId,
        accountName
    );

    try {
        const response = await duplicateCampaignsGroups({
            requestBody: { elements: campaignGroupsWithDuplicationSettings },
        });

        const responseElements = get(response, 'elements');
        const responseCampaignGroups = map(filter(responseElements, ['error', null]), 'value');
        const responseCampaignGroupsErrors = compact(map(responseElements, 'error'));

        if (!isEmpty(responseCampaignGroupsErrors)) {
            // TODO: Best to just show the errors in a results page or so, indications are not equipped for this
            throw head(responseCampaignGroupsErrors);
        }

        dispatch(duplicateCampaignsGroupSuccess(responseCampaignGroups));

        const allFailedCampaignIds = uniq(compact(flatMap(responseCampaignGroups, 'failedCampaignDuplicationIds')));
        if (!isEmpty(allFailedCampaignIds)) {
            dispatch(addIndication(generateFailedCampaignWarningIndication(allFailedCampaignIds.join(', '))));

            return response;
        }

        dispatch(addIndication(successIndication));

        return response;
    } catch (error) {
        dispatch(addIndication(generateErrorIndication(error)));
        dispatch(duplicateCampaignsGroupError(campaignsGroup.campaignsGroupId, error));
        throw error;
    }
};

const transformCampaignGroupsWithDuplicationSettings = (
    campaignsGroup,
    campaignsGroupChanges,
    numericAccountId,
    accountName
) => {
    const { campaignsGroupId } = campaignsGroup;
    const { duplicateCampaignGroupSettings, ...rest } = campaignsGroupChanges;
    const { numberOfCopies, ...restDuplicationSettings } = duplicateCampaignGroupSettings;

    return times(numberOfCopies, index => {
        const { campaignsGroupName } = rest;
        const campaignsGroupNameWithIndex = `${campaignsGroupName}${index > 0 ? ` (${index + 1})` : ''}`;
        return {
            value: {
                campaignsGroup: {
                    ...rest,
                    campaignsGroupId,
                    campaignsGroupName: campaignsGroupNameWithIndex,
                    accountId: accountName,
                    numericAccountId,
                    marketingObjective: campaignsGroup.marketingObjective,
                    kpi: campaignsGroup.kpi,
                    kpiScaleUpThreshold: campaignsGroup.kpiScaleUpThreshold,
                    kpiUnderPerformanceThreshold: campaignsGroup.kpiUnderPerformanceThreshold,
                },
                duplicateCampaignGroupSettings: restDuplicationSettings,
            },
        };
    });
};
